import { useState, useEffect, useCallback } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  Divider,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useCustomerSearch } from "../../../hooks/useCustomerSearch.js";
import {
  fetchAllCustomerImportSettings,
  fetchCustomerImportSettings,
  updateCustomerImportSetting,
} from "@accredo/fe-shared/services/importCustomerSettingService.js";
import ImportCustomerOverviewTable from "./components/ImportCustomerOverviewTable.jsx";

const record_typeS = [
  { value: "kunde", label: "Kunde" },
  { value: "vertrag", label: "Vertrag" },
  { value: "objekt", label: "Objekt" },
  { value: "objektsparte", label: "Objektsparte" },
  { value: "gesellschaft", label: "Gesellschaft" },
  { value: "user", label: "Benutzer" },
  { value: "bank_account", label: "Bankkonto" },
  { value: "user_contract_assignments", label: "Benutzer-Vertragszuordnungen" },
];

export default function ImportCustomerSettingsTab() {
  const [selected_customer, setSelectedCustomer] = useState(null);
  const [settings_map, setSettingsMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState({});

  const [overview, setOverview] = useState([]);
  const [overview_loading, setOverviewLoading] = useState(true);
  const [overview_loaded, setOverviewLoaded] = useState(false);

  const {
    customers,
    loading: search_loading,
    searchCustomers,
  } = useCustomerSearch();

  const loadOverview = useCallback(async () => {
    setOverviewLoading(true);
    try {
      const { data } = await fetchAllCustomerImportSettings();
      setOverview(data);
      setOverviewLoaded(true);
    } catch {
      /* non-critical */
    } finally {
      setOverviewLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const loadSettings = useCallback(async (kunde_id) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await fetchCustomerImportSettings(kunde_id);
      const map = {};
      data.forEach((s) => {
        map[s.record_type] = s.import_mode;
      });
      setSettingsMap(map);
    } catch {
      setError("Fehler beim Laden der Einstellungen.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selected_customer) {
      loadSettings(selected_customer.id);
    } else {
      setSettingsMap({});
      setError(null);
    }
  }, [selected_customer, loadSettings]);

  async function toggle(record_type, current_mode) {
    const new_mode = current_mode === "skip" ? "enabled" : "skip";
    setSettingsMap((prev) => ({ ...prev, [record_type]: new_mode }));
    setSaving((prev) => ({ ...prev, [record_type]: true }));
    try {
      await updateCustomerImportSetting(
        selected_customer.id,
        record_type,
        new_mode
      );
      loadOverview();
    } catch {
      setSettingsMap((prev) => ({ ...prev, [record_type]: current_mode }));
      setError("Fehler beim Speichern.");
    } finally {
      setSaving((prev) => ({ ...prev, [record_type]: false }));
    }
  }

  function selectFromOverview(row) {
    setSelectedCustomer({
      id: row.kunde_id,
      id_ov: row.id_ov,
      label: row.name,
      display: row.name,
    });
  }

  return (
    <Box display="flex" flexDirection="column" gap={4}>
      {/* ── Overview ── */}
      <Box>
        <Typography variant="subtitle2" color="text.secondary" mb={1}>
          Kunden mit abweichenden Import-Einstellungen
        </Typography>
        <ImportCustomerOverviewTable
          data={overview}
          isLoading={overview_loading}
          hasLoaded={overview_loaded}
          selected_kunde_id={selected_customer?.id}
          onRowClick={selectFromOverview}
        />
      </Box>

      <Divider />

      {/* ── Editor ── */}
      <Box>
        <Typography variant="subtitle2" color="text.secondary" mb={1.5}>
          Einstellungen bearbeiten
        </Typography>

        <Autocomplete
          options={customers}
          getOptionLabel={(o) => o.display || ""}
          value={selected_customer}
          onChange={(_, v) => setSelectedCustomer(v)}
          onInputChange={(_, v) => searchCustomers(v)}
          loading={search_loading}
          sx={{ maxWidth: 440 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Kunde auswählen"
              placeholder="Name oder ID eingeben..."
              size="small"
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {search_loading && (
                        <CircularProgress color="inherit" size={16} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
          renderOption={({ key, ...props }, option) => (
            <Box key={key} component="li" {...props}>
              <Box>
                <Typography variant="body2">{option.label}</Typography>
                {option.location && (
                  <Typography variant="caption" color="text.secondary">
                    {option.location} • ID: {option.id_ov}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
          noOptionsText="Keine Kunden gefunden. Mindestens 2 Zeichen eingeben."
        />

        {!selected_customer && (
          <Typography variant="body2" color="text.secondary" mt={2}>
            Wählen Sie einen Kunden aus, um dessen Import-Einstellungen zu
            verwalten.
          </Typography>
        )}

        {selected_customer && (
          <Box mt={2}>
            {error && (
              <Typography color="error" variant="body2" mb={2}>
                {error}
              </Typography>
            )}

            {loading ? (
              <CircularProgress size={24} />
            ) : (
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ maxWidth: 440 }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Entitätstyp</TableCell>
                      <TableCell align="center">Import aktiv</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {record_typeS.map((et) => {
                      const mode = settings_map[et.value] ?? "enabled";
                      const is_saving = !!saving[et.value];
                      return (
                        <TableRow key={et.value}>
                          <TableCell>{et.label}</TableCell>
                          <TableCell align="center">
                            {is_saving ? (
                              <CircularProgress size={20} />
                            ) : (
                              <Switch
                                checked={mode === "enabled"}
                                onChange={() => toggle(et.value, mode)}
                                size="small"
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
